import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, Input as ChakraInput, InputGroup, InputRightElement, Text, useTheme } from '@chakra-ui/react';
import { getInputTypographyOverride, getTextStyleTypographyOverride, useSemanticTypography } from '@whitbread-eos/utils';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { Error, Error24, Success24 } from '../../assets/icons';
import { formatDataTestId } from '../../utils/formatters';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Tooltip = dynamic(_asyncToGenerator(function* () {
  var _yield$import = yield import('../Tooltip/Tooltip.component'),
    Tooltip = _yield$import.default;
  return {
    default: Tooltip
  };
}), {
  ssr: false
});
var stripTrailingAsterisk = text => {
  var trimmed = text === null || text === void 0 ? void 0 : text.trimEnd();
  if (!(trimmed !== null && trimmed !== void 0 && trimmed.endsWith('*'))) return {
    textPart: text,
    hasMark: false
  };
  return {
    textPart: trimmed.slice(0, -1).trimEnd(),
    hasMark: true
  };
};
var hasTrailingAsterisk = text => !!(text !== null && text !== void 0 && text.trimEnd().endsWith('*'));
var getDisplayProps = _ref2 => {
  var _ref3;
  var label = _ref2.label,
    showLabel = _ref2.showLabel,
    isDisabled = _ref2.isDisabled,
    helperText = _ref2.helperText,
    charLimit = _ref2.charLimit,
    error = _ref2.error,
    showIcon = _ref2.showIcon,
    value = _ref2.value;
  return {
    displayLabel: label && (showLabel || !isDisabled),
    displayUnderField: (_ref3 = helperText !== null && helperText !== void 0 ? helperText : charLimit) !== null && _ref3 !== void 0 ? _ref3 : error,
    displayHelperText: helperText && !error || helperText && error && isDisabled,
    displayCharLimit: charLimit && !isDisabled,
    displayIcon: showIcon && !isDisabled,
    isValid: !error && value
  };
};
export default function Input(props) {
  var theme = useTheme();
  var _useTranslation = useTranslation(['common']),
    t = _useTranslation.t;
  var _props$autoComplete = props.autoComplete,
    autoComplete = _props$autoComplete === void 0 ? 'on' : _props$autoComplete,
    _props$type = props.type,
    type = _props$type === void 0 ? 'text' : _props$type,
    label = props.label,
    placeholderText = props.placeholderText,
    name = props.name,
    showLabel = props.showLabel,
    isDisabled = props.isDisabled,
    isAutoFocused = props.isAutoFocused,
    helperText = props.helperText,
    charLimit = props.charLimit,
    maxLength = props.maxLength,
    error = props.error,
    useTooltip = props.useTooltip,
    useCustomTooltip = props.useCustomTooltip,
    value = props.value,
    onChange = props.onChange,
    onBlur = props.onBlur,
    onClick = props.onClick,
    onKeyDown = props.onKeyDown,
    _props$showRightEleme = props.showRightElement,
    showRightElement = _props$showRightEleme === void 0 ? false : _props$showRightEleme,
    _props$showIcon = props.showIcon,
    showIcon = _props$showIcon === void 0 ? false : _props$showIcon,
    _props$showText = props.showText,
    showText = _props$showText === void 0 ? 'Show' : _props$showText,
    _props$hideText = props.hideText,
    hideText = _props$hideText === void 0 ? 'Hide' : _props$hideText,
    inputRef = props.inputRef,
    className = props.className,
    styles = props.styles,
    _props$isInputAriaReq = props.isInputAriaRequired,
    isInputAriaRequired = _props$isInputAriaReq === void 0 ? false : _props$isInputAriaReq,
    _props$extraTooltipSt = props.extraTooltipStyles,
    extraTooltipStyles = _props$extraTooltipSt === void 0 ? {} : _props$extraTooltipSt,
    _props$emailFieldProp = props.emailFieldProps,
    emailFieldProps = _props$emailFieldProp === void 0 ? {} : _props$emailFieldProp;
  var getTypographyProps = useSemanticTypography();
  var _getDisplayProps = getDisplayProps({
      label,
      showLabel,
      isDisabled,
      helperText,
      charLimit,
      error,
      showIcon,
      value
    }),
    displayLabel = _getDisplayProps.displayLabel,
    displayUnderField = _getDisplayProps.displayUnderField,
    displayHelperText = _getDisplayProps.displayHelperText,
    displayCharLimit = _getDisplayProps.displayCharLimit,
    displayIcon = _getDisplayProps.displayIcon,
    isValid = _getDisplayProps.isValid;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPasswordVisible = _useState2[0],
    setIsPasswordVisible = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    focused = _useState4[0],
    setFocused = _useState4[1];
  var handleFocus = val => setFocused(val);
  var handleClick = () => setIsPasswordVisible(!isPasswordVisible);
  var baseDataTestId = 'input';
  var labelParts = stripTrailingAsterisk(label);
  var placeholderParts = stripTrailingAsterisk(placeholderText);
  var isRequired = isInputAriaRequired || labelParts.hasMark || hasTrailingAsterisk(placeholderText);
  var accessibleLabelText = labelParts.textPart || placeholderParts.textPart || undefined;
  var requiredLabelSuffix = t('cardNotPresent.memorableWordRequired', {
    defaultValue: 'required'
  });
  var accessibleLabel = isRequired && accessibleLabelText ? "".concat(accessibleLabelText, " ").concat(requiredLabelSuffix) : undefined;
  var wrapperStyles = _objectSpread(_objectSpread({}, inputWrapperStyle), styles === null || styles === void 0 ? void 0 : styles.inputWrapperStyles);
  var inputElementStyles = _objectSpread(_objectSpread({}, inputStyle(error)), styles === null || styles === void 0 ? void 0 : styles.inputElementStyles);
  var inputTypographyOverride = getInputTypographyOverride(styles === null || styles === void 0 ? void 0 : styles.inputElementStyles, theme === null || theme === void 0 ? void 0 : theme.textStyles);
  var labelTypographyOverride = getTextStyleTypographyOverride(styles === null || styles === void 0 ? void 0 : styles.labelElementStyles, theme === null || theme === void 0 ? void 0 : theme.textStyles);
  var labelElementStyles = _objectSpread(_objectSpread(_objectSpread({}, labelStyle(error, value)), styles === null || styles === void 0 ? void 0 : styles.labelElementStyles), labelTypographyOverride);
  return _jsxs(FormControl, _objectSpread(_objectSpread({}, wrapperStyles), {}, {
    isInvalid: error && !isDisabled,
    children: [displayLabel && _jsx(FormLabel, _objectSpread(_objectSpread({
      "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-label")),
      pos: "absolute"
    }, labelElementStyles), {}, {
      htmlFor: name,
      children: label
    })), _jsx(Tooltip, _objectSpread(_objectSpread(_objectSpread({}, extraTooltipStyles), tooltipStyle), {}, {
      description: error,
      variant: "inlineError",
      placement: "bottom-start",
      isDisabled: !error || !useTooltip,
      isOpen: error && useTooltip,
      svg: _jsx(Error24, {}),
      children: _jsxs(InputGroup, {
        children: [_jsx(ChakraInput, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
          autoComplete: autoComplete,
          "data-testid": formatDataTestId(baseDataTestId, "".concat(name))
        }, inputElementStyles), inputTypographyOverride), {}, {
          errorBorderColor: error && 'error',
          maxLength: maxLength !== null && maxLength !== void 0 ? maxLength : charLimit,
          ref: inputRef,
          isDisabled: isDisabled,
          id: name,
          placeholder: placeholderText,
          value: value,
          onChange: handleChange,
          onBlur: onBlur,
          onBlurCapture: () => handleFocus(false),
          autoFocus: isAutoFocused,
          type: type,
          className: className !== null && className !== void 0 ? className : '',
          onFocus: () => handleFocus(true),
          onClick: onClick,
          onKeyDown: onKeyDown
        }, emailFieldProps), {}, {
          "aria-label": accessibleLabel,
          "aria-required": isRequired ? true : undefined
        })), type === 'password' && showRightElement && _jsx(InputRightElement, {
          width: "4.5rem",
          children: _jsx(Text, {
            "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-SwitchShowPass")),
            h: "1.75rem",
            size: "sm",
            onClick: handleClick,
            children: isPasswordVisible ? hideText : showText
          })
        }), displayIcon && error && !focused && _jsx(Box, _objectSpread(_objectSpread({}, iconStyle), {}, {
          "data-testid": "inputIconError",
          children: _jsx(Error24, {})
        })), displayIcon && isValid && !focused && _jsx(Box, _objectSpread(_objectSpread({}, iconStyle), {}, {
          "data-testid": "inputIconSuccess",
          children: _jsx(Success24, {})
        }))]
      })
    })), displayUnderField && _jsxs(Flex, {
      w: "full",
      dir: "column",
      justify: !helperText && !error ? 'flex-end' : 'space-between',
      children: [error && !useTooltip && renderFieldErrorMessage(), displayHelperText && _jsx(FormHelperText, _objectSpread(_objectSpread({
        "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-FormHelperText"))
      }, helperTextStyle(isDisabled)), {}, {
        children: helperText
      })), handleDisplayCharLimit(displayCharLimit, baseDataTestId, error, value, charLimit, name)]
    })]
  }));
  function handleChange(event) {
    var _event$target;
    if (onChange) onChange(event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
  }
  function renderFieldErrorMessage() {
    var typographyProps = getTypographyProps({
      fontSize: 'xs'
    }, {
      textStyle: 'body-s-regular'
    });
    var errorTypographyProps = getTextStyleTypographyOverride(typographyProps, theme === null || theme === void 0 ? void 0 : theme.textStyles);
    var errorMsgStyle = _objectSpread(_objectSpread(_objectSpread({}, typographyProps), errorTypographyProps), {}, {
      color: 'error',
      ml: 'md',
      mt: 'sm'
    });
    return !useCustomTooltip ? typeof error !== 'boolean' && _jsx(FormErrorMessage, _objectSpread(_objectSpread({
      "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-FormErrorMessage"))
    }, errorMsgStyle), {}, {
      children: error
    })) : _jsxs(FormErrorMessage, _objectSpread(_objectSpread({
      "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-FormErrorMessage"))
    }, customErrorMsgStyle), {}, {
      children: [_jsx(Error, {}), _jsx(Box, {
        pl: "0.5rem",
        children: error
      })]
    }));
  }
}
var handleDisplayCharLimit = (displayCharLimit, baseDataTestId, error, value, charLimit, name) => {
  if (displayCharLimit) {
    var _value$length;
    return _jsxs(Text, _objectSpread(_objectSpread({
      "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-CharLimit"))
    }, charLimitStyle(error)), {}, {
      children: [(_value$length = value === null || value === void 0 ? void 0 : value.length) !== null && _value$length !== void 0 ? _value$length : 0, "/", charLimit]
    }));
  }
};
var inputWrapperStyle = {
  borderRadius: 'xs',
  zIndex: 0,
  borderColor: 'lightGrey1',
  _hover: {
    borderColor: 'darkGrey1'
  },
  _focus: {
    zIndex: '0'
  }
};
var inputStyle = error => {
  return {
    h: '3.5rem',
    zIndex: 0,
    borderWidth: '1px',
    borderColor: 'var(--chakra-colors-lightGrey1)',
    _hover: {
      borderColor: 'none'
    },
    _focus: {
      zIndex: '0',
      borderWidth: '2px',
      borderColor: error ? 'error' : 'primary'
    },
    _placeholder: {
      color: 'darkGrey2',
      fontStyle: 'normal',
      _focus: {
        color: 'success'
      },
      _hover: {
        color: 'primary'
      }
    },
    _autofill: {
      boxShadow: '0 0 0 1000px #FFFFFF inset'
    }
  };
};
var labelStyle = (error, value) => {
  return {
    h: '1.25rem',
    w: 'fit-content',
    fontSize: 'sm',
    align: 'center',
    px: 'xs',
    fontWeight: 'normal',
    ml: '0.750rem',
    top: '-0.625rem',
    backgroundColor: 'baseWhite',
    zIndex: '1',
    color: error ? 'error' : 'darkGrey1',
    display: value ? 'block' : 'none',
    _focus: {
      color: error ? 'error' : 'primary',
      display: 'block'
    },
    lineHeight: '1'
  };
};
var helperTextStyle = isDisabled => {
  return {
    fontSize: 'xs',
    color: isDisabled ? 'lightGrey1' : 'darkGrey1',
    ml: 'md',
    mt: '0.375rem'
  };
};
var charLimitStyle = error => {
  return {
    fontSize: 'xs',
    color: error ? 'error' : 'darkGrey2',
    mr: 'sm',
    mt: '0.375rem'
  };
};
var customErrorMsgStyle = {
  fontSize: 'sm',
  mt: 'xmd',
  w: 'full',
  h: '2.5rem',
  bgColor: 'tooltipError',
  color: 'darkGrey1',
  padding: '1rem',
  borderRadius: 'var(--chakra-space-xs)',
  _before: {
    content: '" "',
    position: 'absolute',
    top: '4rem',
    left: '1.531rem',
    width: 'var(--chakra-space-xmd)',
    height: 'var(--chakra-space-xmd)',
    transform: 'rotate(-45deg)',
    bgColor: 'tooltipError'
  }
};
var tooltipStyle = {
  h: '2.25rem',
  display: 'flex',
  alignContent: 'center'
};
var iconStyle = {
  ml: 'md',
  alignSelf: 'center'
};